import * as React from 'react';
import { __ } from '../../helpers/TranslationService';
import styled from '@emotion/styled';
import { BreakpointsEnum } from '../../helpers/breakpoints';
import { IPageDataReducer } from '../../reducers/pageData';
import { ICruiseRecommendation } from '../../interfaces/ISearchApi';
import { ITrackingData } from '../../helpers/EventTracker/EventTracker';
import { PriceWithDiscount } from './PriceWithDiscount';
import { CruiseRecommendationDescRegular } from './CruiseRecommendationDescRegular';

import { format } from 'date-fns';
export interface IProps {
    pageData: IPageDataReducer;
    cruiseRecommendations: ICruiseRecommendation[];
    trackingData: ITrackingData;
    isShipImage?: boolean;
    discountThreshold?: number;
}

const TeaserCardStyled = styled.div`
    padding: 10px 10px 5px;
    overflow: hidden;
    display: flex;
    align-items: center;
`;

const TeaserTitleStyled = styled.div`
    font-size: 19px;
    line-height: 28px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.white[100]};
    text-align: center;
    position: absolute;
    width: 100%;
    bottom: 0;
    margin: 0;
    padding: 10px 6px;
    text-shadow: 0 0 4px ${({ theme }) => theme.colors.black[100]};
    background: linear-gradient(to bottom, rgba(8, 29, 46, 0), rgba(1, 47, 74, 0.71));
`;

const FeatureList = styled.ul`
    padding: 0px 10px 10px;
    margin: 0;
`;

const FeatureListItem = styled.li`
    color: ${({ theme }) => theme.colors.black[100]};
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
    text-overflow: ellipsis;
    position: relative;
    padding-left: 22px;
    margin-top: 4px;
    overflow: hidden;
    @media (max-width: ${BreakpointsEnum.md}) {
        white-space: pre-wrap;
    }
    &::before {
        content: '';
        position: absolute;
        top: 6px;
        left: 0;
        width: 10px;
        height: 6px;
        border-style: solid;
        border-width: 0 0 3px 3px;
        border-color: ${({ theme }) => theme.colors.yellowSea[100]};
        transform: rotate(-45deg);
    }
`;

export function cruiseList(props: IProps) {
    const { cruiseRecommendations } = props;
    const cruiseSlug = props.pageData.brandConfig.slugsObj.cruisepage;
    const cruiseSlugPattern = new RegExp(`\/${cruiseSlug}(.*?)\/`);

    return cruiseRecommendations.slice(0, 6).map((val, key) => {
        const earliestDepartureDate = new Date(parseInt(val.earliestDeparture, 10) * 1000);

        const earliestDeparture = isNaN(earliestDepartureDate.getTime())
            ? ''
            : format(new Date(parseInt(val.earliestDeparture, 10) * 1000), 'MM/yyyy');

        const latestArrivalDate = new Date(parseInt(val.latestArrival, 10) * 1000);

        const latestArrival = isNaN(latestArrivalDate.getTime())
            ? ''
            : format(new Date(parseInt(val.latestArrival, 10) * 1000), 'MM/yyyy');

        const calendar =
            latestArrival === earliestDeparture || latestArrival === ''
                ? earliestDeparture
                : `${earliestDeparture} - ${latestArrival}`;

        const region = val.region.replace(/,/, ', ');
        const cruisePageUrlSuffix = val.cruisePageUrl.split(cruiseSlugPattern);
        const cruiseSlugSuffix =
            cruisePageUrlSuffix.length === 3 ? cruisePageUrlSuffix[2] : val.cruisePageUrl;
        const cruisePageUrl = `/${cruiseSlug}/${cruiseSlugSuffix}`;
        const priceProps = {
            price: val.lowestPriceDualCurrency || val.lowestPrice,
            discount: val.maxDiscount,
            currency: val.dualCurrency || val.currency,
            discountThreshold: props.discountThreshold,
        };
        const featuresList = val.featureBulletPoints?.length > 0 ? val.featureBulletPoints : null;

        const child = (
            <>
                <TeaserCardStyled>
                    <CruiseRecommendationDescRegular region={region} calendar={calendar} />
                    <PriceWithDiscount {...priceProps} />
                </TeaserCardStyled>
                {featuresList && (
                    <FeatureList>
                        {featuresList.map((item) => (
                            <FeatureListItem key={item} title={item}>
                                {item}
                            </FeatureListItem>
                        ))}
                    </FeatureList>
                )}
            </>
        );

        const teaserTitle = <TeaserTitleStyled dangerouslySetInnerHTML={{ __html: val.title }} />;

        const shipImageSrc = props.isShipImage ? val.shipImage : val.teaserImage;
        const imageRoute = val?.routeMapImage;
        const featuredImage = val?.featuredImage;

        const imageSrc = imageRoute || shipImageSrc || featuredImage;

        const trackingData = {
            category: props.trackingData.category ? props.trackingData.category : '',
            action: `teaser ${key + 1}`,
            label: props.trackingData.label
                ? `${props.trackingData.label}-${val.shipTitle}`
                : val.shipTitle,
        };
        return {
            child: child,
            url: cruisePageUrl,
            companyImage: val.companyImage,
            trackingData: trackingData,
            teaserTitle: teaserTitle,
            imageSrc: imageSrc,
            imageAlt: val.title,
            nid: val.cruiseNid,
        };
    });
}
